import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaSignOutAlt,
  FaShoppingCart,
  FaEuroSign,
  FaUsers,
  FaChartLine,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext.tsx";
import { database } from "../../firebase/config.ts";
import { ref, onValue, remove } from "firebase/database";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Order } from "../../types/index.ts";
import "./Dashboard.css";

interface Stats {
  totalOrders: number;
  totalRevenue: number;
  todayOrders: number;
  avgOrderValue: number;
}

const Dashboard: React.FC = () => {
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalOrders: 0,
    totalRevenue: 0,
    todayOrders: 0,
    avgOrderValue: 0,
  });
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const toggleOrder = (orderId: string) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
    } else {
      setExpandedOrderId(orderId);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate("/admin");
      return;
    }

    const ordersRef = ref(database, "orders");
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ordersArray = Object.entries(data).map(
          ([id, order]: [string, any]) => ({
            id,
            ...order,
          })
        );

        setOrders(ordersArray);

        const totalRevenue = ordersArray.reduce(
          (sum, order) => sum + order.total,
          0
        );
        const today = new Date().toDateString();
        const todayOrders = ordersArray.filter(
          (order) => new Date(order.orderDate).toDateString() === today
        ).length;

        setStats({
          totalOrders: ordersArray.length,
          totalRevenue,
          todayOrders,
          avgOrderValue:
            ordersArray.length > 0 ? totalRevenue / ordersArray.length : 0,
        });
      }
    });

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/admin");
    } catch (error) {
      console.error("Erreur de déconnexion:", error);
    }
  };

  const getChartData = () => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      });

      const dayOrders = orders.filter((order) => {
        const orderDate = new Date(order.orderDate);
        return orderDate.toDateString() === date.toDateString();
      });

      last7Days.push({
        date: dateStr,
        commandes: dayOrders.length,
        revenue: dayOrders.reduce((sum, order) => sum + order.total, 0),
      });
    }
    return last7Days;
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Tableau de Bord</h1>
              <p>Bienvenue, {currentUser?.email}</p>
            </div>
            <div className="header-actions">
              <motion.button
                className="btn-add"
                onClick={() => navigate("/admin/products")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPlus /> Produits
              </motion.button>

              <motion.button
                className="btn-settings"
                onClick={() => navigate("/admin/settings")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ⚙️ Paramètres
              </motion.button>

              <motion.button
                className="btn-logout"
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaSignOutAlt /> Déconnexion
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="container dashboard-content">
        <div className="stats-grid">
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="stat-icon" style={{ background: "#FF6B35" }}>
              <FaShoppingCart />
            </div>
            <div className="stat-info">
              <h3>{stats.totalOrders}</h3>
              <p>Total Commandes</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="stat-icon" style={{ background: "#27AE60" }}>
              <FaEuroSign />
            </div>
            <div className="stat-info">
              <h3>{stats.totalRevenue.toFixed(2)}€</h3>
              <p>Revenu Total</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="stat-icon" style={{ background: "#3498DB" }}>
              <FaUsers />
            </div>
            <div className="stat-info">
              <h3>{stats.todayOrders}</h3>
              <p>Commandes Aujourd'hui</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="stat-icon" style={{ background: "#9B59B6" }}>
              <FaChartLine />
            </div>
            <div className="stat-info">
              <h3>{stats.avgOrderValue.toFixed(2)}€</h3>
              <p>Panier Moyen</p>
            </div>
          </motion.div>
        </div>

        <div className="charts-grid">
          <motion.div
            className="chart-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2>Commandes des 7 derniers jours</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="commandes"
                  stroke="#FF6B35"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            className="chart-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2>Revenus des 7 derniers jours</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#27AE60" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <motion.div
          className="orders-table-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <h2>Dernières Commandes</h2>
          <div className="orders-list">
            {orders
              .slice(0, 10)
              .reverse()
              .map((order) => (
                <motion.div
                  key={order.id}
                  className="order-detail-card"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => order.id && toggleOrder(order.id)}
                >
                  <div className="order-header-row">
                    <div className="order-id">
                      <strong>
                        Commande #{order.id?.slice(0, 8) || "N/A"}
                      </strong>
                      <span className="order-date">
                        {new Date(order.orderDate).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <div className="order-badges">
                      <span
                        className={`badge ${
                          order.deliveryType === "delivery"
                            ? "badge-delivery"
                            : "badge-pickup"
                        }`}
                      >
                        {order.deliveryType === "delivery"
                          ? "🚚 Livraison"
                          : "🏪 Retrait"}
                      </span>
                      <span
                        className={`badge ${
                          order.paymentMethod === "card"
                            ? "badge-card"
                            : "badge-cash"
                        }`}
                      >
                        {order.paymentMethod === "card"
                          ? "💳 Carte"
                          : "💵 Espèces"}
                      </span>
                    </div>
                    <div className="order-total-display">
                      <span>Total:</span>
                      <strong>{order.total.toFixed(2)}€</strong>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedOrderId === order.id && (
                      <motion.div
                        className="order-expanded-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="order-info-grid">
                          <div className="info-section">
                            <h4>👤 Client</h4>
                            <p>
                              <strong>{order.formData?.name || "N/A"}</strong>
                            </p>
                            <p>📞 {order.formData?.phone || "N/A"}</p>
                            <p>📧 {order.formData?.email || "N/A"}</p>
                          </div>

                          {order.deliveryType === "delivery" && (
                            <div className="info-section">
                              <h4>📍 Adresse de livraison</h4>
                              <p>{order.formData?.address || "N/A"}</p>
                              <p>
                                {order.formData?.postalCode}{" "}
                                {order.formData?.city}
                              </p>
                              {order.formData?.latitude &&
                                order.formData?.longitude && (
                                  <a
                                    href={`https://www.google.com/maps?q=${order.formData.latitude},${order.formData.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="map-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    🗺️ Voir sur la carte
                                  </a>
                                )}
                            </div>
                          )}

                          <div className="info-section full-width-mobile">
                            <h4>🛒 Articles</h4>
                            <div className="admin-cart-items">
                              {order.cart?.map((item, idx) => (
                                <div key={idx} className="admin-cart-item">
                                  <div className="admin-item-image">
                                    <img src={item.image} alt={item.name} />
                                  </div>
                                  <div className="admin-item-details">
                                    <div className="item-header">
                                      <span className="item-quantity">
                                        {item.quantity}x
                                      </span>
                                      <span className="item-name">
                                        {item.name}
                                      </span>
                                    </div>
                                    {item.options &&
                                      item.options.length > 0 && (
                                        <div className="item-options">
                                          {item.options
                                            .map((opt) => opt.name)
                                            .join(", ")}
                                        </div>
                                      )}
                                  </div>
                                  <div className="admin-item-price">
                                    {(
                                      item.price * item.quantity +
                                      (item.options?.reduce(
                                        (sum, opt) => sum + opt.price,
                                        0
                                      ) || 0) *
                                        item.quantity
                                    ).toFixed(2)}
                                    €
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {order.formData?.notes && (
                            <div className="info-section">
                              <h4>📝 Notes</h4>
                              <p className="order-notes">
                                {order.formData.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {expandedOrderId !== order.id && (
                    <div
                      className="click-hint"
                      style={{
                        marginTop: "10px",
                        textAlign: "center",
                        fontSize: "12px",
                        color: "#999",
                      }}
                    >
                      Cliquez pour voir les détails ▼
                    </div>
                  )}
                </motion.div>
              ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
